/**
 * 1:1 port of packages/amgbaccarat_v2/lib/domain/game_engine/result_parser.dart.
 *
 * Input format (variable length, comma- or whitespace-delimited tokens):
 *   "P|clubs-8, P|hearts-9, B|diamonds-3, B|spades-6, #7, #9, @265,268, NeedShuffle"
 *
 * Token conventions:
 *   P|suit-rank     — Player card. Up to 3 P tokens (3rd is optional).
 *   B|suit-rank     — Banker card. Up to 3 B tokens (3rd is optional).
 *   #N              — Score. First `#` is player score, second is banker score.
 *   @id1,id2,...    — Winning bet item IDs (comma-list).
 *   NeedShuffle     — Server flag (informational; we ignore).
 *
 * Output card numbers are 1..52 using SUIT_OFFSET; 0 means slot is absent.
 */
import type { CardSuit, GameResult } from '../types/game';
import {
  BET_ITEM_ID,
  SUIT_OFFSET,
  gameResultFromWinningBetIds,
} from '../types/game';

export interface ParsedResult {
  /** Always 3 entries; trailing zeros mean "no third card". */
  playerCards: number[];
  bankerCards: number[];
  playerScore: number;
  bankerScore: number;
  winningBetIds: number[];
  gameResult: GameResult;
  needShuffle: boolean;
}

export function parseResultString(raw: string): ParsedResult {
  // Split on comma OR whitespace; collapse empties.
  const tokens = raw
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  const playerCards: number[] = [];
  const bankerCards: number[] = [];
  const scores: number[] = [];
  let winningBetIds: number[] = [];
  let needShuffle = false;

  for (const tok of tokens) {
    if (tok === 'NeedShuffle') {
      needShuffle = true;
      continue;
    }
    if (tok.startsWith('P|')) {
      playerCards.push(parseCardToNumber(tok.slice(2)));
      continue;
    }
    if (tok.startsWith('B|')) {
      bankerCards.push(parseCardToNumber(tok.slice(2)));
      continue;
    }
    if (tok.startsWith('#')) {
      const n = parseInt(tok.slice(1), 10);
      if (Number.isFinite(n)) scores.push(n);
      continue;
    }
    if (tok.startsWith('@')) {
      // Winning IDs can arrive in either form:
      //   "@265,268"  → one token with a comma-list, OR
      //   "@266,@268" → one `@`-prefixed token per ID (each hits this branch).
      // ACCUMULATE across tokens — overwriting here dropped all but the last
      // ID, which made e.g. "@266,@268" (banker + player-pair) collapse to just
      // [268], leaving no main-bet ID so the winner defaulted to TIE.
      winningBetIds.push(...parseWinningIdsHead(tok));
      continue;
    }
    // Continuation of an `@...,N` list — when comma split breaks the list.
    if (winningBetIds.length > 0 && /^\d+$/.test(tok)) {
      winningBetIds.push(parseInt(tok, 10));
      continue;
    }
  }

  while (playerCards.length < 3) playerCards.push(0);
  while (bankerCards.length < 3) bankerCards.push(0);

  const playerScore = scores[0] ?? 0;
  const bankerScore = scores[1] ?? 0;

  // Validate at least one ID hits a main bet — engine adds player/banker/tie
  // to the winning list (bet_constants.dart). Fall back to score comparison.
  const fallbackIds: number[] = [];
  if (winningBetIds.length === 0) {
    if (playerScore > bankerScore) fallbackIds.push(BET_ITEM_ID.player);
    else if (bankerScore > playerScore) fallbackIds.push(BET_ITEM_ID.banker);
    else fallbackIds.push(BET_ITEM_ID.tie);
    winningBetIds = fallbackIds;
  }

  return {
    playerCards,
    bankerCards,
    playerScore,
    bankerScore,
    winningBetIds,
    gameResult: gameResultFromWinningBetIds(winningBetIds),
    needShuffle,
  };
}

function parseCardToNumber(card: string): number {
  const parts = card.split('-');
  if (parts.length !== 2) throw new Error(`Invalid card format: ${card}`);
  const suit = parts[0].toLowerCase() as CardSuit;
  if (!(suit in SUIT_OFFSET)) throw new Error(`Invalid suit: ${suit}`);
  const value = parseInt(parts[1], 10);
  if (!Number.isFinite(value) || value < 1 || value > 13) {
    throw new Error(`Invalid card value: ${parts[1]}`);
  }
  return SUIT_OFFSET[suit] + value;
}

function parseWinningIdsHead(token: string): number[] {
  // token is "@265" or "@265,268" — depending on whether the upstream
  // split included or stripped the inner comma.
  const body = token.slice(1);
  const out: number[] = [];
  for (const part of body.split(',')) {
    const n = parseInt(part, 10);
    if (Number.isFinite(n)) out.push(n);
  }
  return out;
}

/**
 * Count how many cards are actually present (slot value > 0).
 */
export function cardCount(slots: number[]): number {
  return slots.reduce((n, v) => (v > 0 ? n + 1 : n), 0);
}
